<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold">Управление заказами</h1>

    <!-- Фильтры -->
    <div class="bg-white p-4 rounded-lg shadow">
      <div class="grid grid-cols-4 gap-4">
        <!-- Статус -->
        <div>
          <label class="block text-sm font-bold mb-2">Статус</label>
          <select
            v-model="filters.status"
            @change="loadOrders"
            class="w-full border px-3 py-2 rounded"
          >
            <option value="">Все статусы</option>
            <option value="pending">Ожидание</option>
            <option value="accepted">Принят</option>
            <option value="in-progress">В работе</option>
            <option value="completed">Завершен</option>
            <option value="cancelled">Отменен</option>
          </select>
        </div>

        <!-- Платеж -->
        <div>
          <label class="block text-sm font-bold mb-2">Платеж</label>
          <select
            v-model="filters.paymentStatus"
            @change="loadOrders"
            class="w-full border px-3 py-2 rounded"
          >
            <option value="">Все платежи</option>
            <option value="unpaid">Не оплачен</option>
            <option value="paid">Оплачен</option>
            <option value="partial">Частично</option>
          </select>
        </div>

        <!-- Поиск по номеру -->
        <div>
          <label class="block text-sm font-bold mb-2">Номер заказа</label>
          <input
            v-model="filters.search"
            @keyup.enter="loadOrders"
            type="text"
            placeholder="ORD-260327-00001"
            class="w-full border px-3 py-2 rounded"
          />
        </div>

        <!-- Кнопка поиска -->
        <div class="flex items-end">
          <button
            @click="loadOrders"
            class="w-full bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700"
          >
            🔍 Поиск
          </button>
        </div>
      </div>
    </div>

    <!-- Таблица заказов -->
    <div class="bg-white rounded-lg shadow">
      <div class="p-6 border-b">
        <h2 class="text-xl font-bold">Заказы ({{ orders.length }})</h2>
      </div>

      <div v-if="loading" class="p-6 text-center text-gray-600">
        Загрузка заказов...
      </div>

      <div v-else-if="orders.length === 0" class="p-6 text-center text-gray-600">
        Заказов не найдено
      </div>

      <table v-else class="w-full">
        <thead class="bg-gray-50 border-b">
          <tr>
            <th class="px-6 py-3 text-left text-sm font-bold">№ Заказа</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Пользователь</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Устройство</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Сумма</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Статус</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Платеж</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Дата</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Действие</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr v-for="order in orders" :key="order._id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-bold">
              <span class="text-blue-600">{{ order.orderNumber }}</span>
            </td>
            <td class="px-6 py-4">
              <div>
                <p class="font-bold">{{ order.user?.name }}</p>
                <p class="text-sm text-gray-600">{{ order.user?.phone }}</p>
              </div>
            </td>
            <td class="px-6 py-4 text-sm">
              <p class="font-bold">{{ order.deviceInfo.brand }} {{ order.deviceInfo.model }}</p>
              <p class="text-gray-600">{{ order.deviceInfo.issue }}</p>
            </td>
            <td class="px-6 py-4 font-bold">₴{{ order.totalPrice }}</td>
            <td class="px-6 py-4">
              <span :class="getStatusClass(order.status)" class="px-3 py-1 rounded-full text-sm font-bold">
                {{ getStatusLabel(order.status) }}
              </span>
            </td>
            <td class="px-6 py-4">
              <span :class="getPaymentClass(order.paymentStatus)" class="px-3 py-1 rounded-full text-sm font-bold">
                {{ getPaymentLabel(order.paymentStatus) }}
              </span>
            </td>
            <td class="px-6 py-4 text-sm text-gray-600">
              {{ formatDate(order.createdAt) }}
            </td>
            <td class="px-6 py-4">
              <router-link
                :to="{ name: 'AdminOrderDetail', params: { id: order._id } }"
                class="text-blue-600 hover:underline text-sm font-bold"
              >
                Подробнее →
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useAuthStore } from '../../stores/auth';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { uk } from 'date-fns/locale';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default {
  name: 'AdminOrders',
  setup() {
    const authStore = useAuthStore();
    const orders = ref([]);
    const loading = ref(false);

    const filters = ref({
      status: '',
      paymentStatus: '',
      search: ''
    });

    const statusLabels = {
      pending: 'Ожидание',
      accepted: 'Принят',
      'in-progress': 'В работе',
      completed: 'Завершен',
      cancelled: 'Отменен'
    };

    const paymentStatusLabels = {
      unpaid: 'Не оплачен',
      paid: 'Оплачен',
      partial: 'Частично'
    };

    const loadOrders = async () => {
      loading.value = true;
      try {
        let url = `${API_URL}/orders`;
        const params = [];

        if (filters.value.status) {
          params.push(`status=${filters.value.status}`);
        }
        if (filters.value.paymentStatus) {
          params.push(`paymentStatus=${filters.value.paymentStatus}`);
        }

        if (params.length > 0) {
          url += '?' + params.join('&');
        }

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${authStore.token}`
          }
        });

        // Фильтруем по поиску на клиенте
        let filtered = response.data;
        if (filters.value.search) {
          filtered = filtered.filter(order =>
            order.orderNumber.toLowerCase().includes(filters.value.search.toLowerCase())
          );
        }

        orders.value = filtered;
      } catch (error) {
        console.error('Ошибка при загрузке заказов:', error);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (date) => {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: uk });
    };

    const getStatusLabel = (status) => statusLabels[status] || status;
    const getStatusClass = (status) => {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-700',
        accepted: 'bg-blue-100 text-blue-700',
        'in-progress': 'bg-purple-100 text-purple-700',
        completed: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700'
      };
      return classes[status] || 'bg-gray-100 text-gray-700';
    };

    const getPaymentLabel = (status) => paymentStatusLabels[status] || status;
    const getPaymentClass = (status) => {
      const classes = {
        unpaid: 'bg-red-100 text-red-700',
        paid: 'bg-green-100 text-green-700',
        partial: 'bg-yellow-100 text-yellow-700'
      };
      return classes[status] || 'bg-gray-100 text-gray-700';
    };

    onMounted(() => {
      loadOrders();
    });

    return {
      orders,
      loading,
      filters,
      loadOrders,
      formatDate,
      getStatusLabel,
      getStatusClass,
      getPaymentLabel,
      getPaymentClass
    };
  }
};
</script>
